import React, { useEffect, useRef, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { rccColors } from "../colors";
import D3GroupedBar from "./D3GroupedBar";
import { appService } from "../../App/app.service";
import { getDefaultSalesType } from "../../util/customFunctions";
import { getNPriorQuarters } from "../../util/fiscalQuarterFunctions";
import { fetchFirstLevelTeamNames, fetchDefaultTeamFilter } from "../rccUtils";
import RccCard from "../RccCard";

/**
 * Displays win rate trends over the last 4 quarters.
 * Inputs: Redux state (funnelFilters, company, user).
 * Outputs: Grouped bar chart of win rates by count and ACV.
 */
function WinRateTrend({ title, redirectToDashboard }) {
  const dispatch = useDispatch();
  const fetchingRef = useRef(false);

  // Get data from Redux store
  const company = useSelector((state) => state.app.company);
  const user = useSelector((state) => state.app.user || {});
  const funnelFilters = useSelector((state) => state.app.funnelFilters || "");
  const valueLabel = useSelector((state) => {
    const label = state.app.user?.Value_Label;
    return typeof label === "string" && label.trim() ? label.trim() : "ACV";
  });

  // Local state for win rate data by quarter
  const [winRateDataByQuarter, setWinRateDataByQuarter] = useState({});
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [firstLevelTeamNames, setFirstLevelTeamNames] = useState([]);
  const [defaultTeamFilter, setDefaultTeamFilter] = useState([]);
  const [isFirstLevelTeamsLoaded, setIsFirstLevelTeamsLoaded] = useState(false);
  const [isDefaultTeamLoaded, setIsDefaultTeamLoaded] = useState(false);

  useEffect(() => {
    fetchFirstLevelTeamNames().then((names) => {
      setFirstLevelTeamNames(names);
      setIsFirstLevelTeamsLoaded(true);
    });
    fetchDefaultTeamFilter()
      .then((teams) => {
        setDefaultTeamFilter(teams);
        setIsDefaultTeamLoaded(true);
      })
      .catch((e) => {
        console.error("Error fetching default team filter:", e);
        setIsDefaultTeamLoaded(true);
      });
  }, []);

  // Check if data is loading
  // Stop loading if filters are fetched but user currentFiscalQuarter is missing
  const isLoading =
    funnelFilters === "" ||
    !isFirstLevelTeamsLoaded ||
    !isDefaultTeamLoaded ||
    (user.currentFiscalQuarter &&
      Object.keys(winRateDataByQuarter).length === 0);

  // Fetch filters and data for each quarter
  useEffect(() => {
    // Step 1: Fetch filters if not available
    if (funnelFilters === "" && !fetchingRef.current) {
      fetchingRef.current = true;
      dispatch({ type: "get_funnel_filters_request" });
      appService.getFunnelFiltersNew().then(
        (resp) => {
          // Reset ref BEFORE dispatching to avoid race condition on re-render.
          // If we dispatch first, the re-render triggers the effect again while fetchingRef is still true.
          fetchingRef.current = false;
          dispatch({
            type: "get_funnel_filters_success",
            json: resp,
          });
        },
        (error) => {
          fetchingRef.current = false;
          dispatch({
            type: "get_funnel_filters_failure",
            error: typeof error === "object" ? "Something went wrong" : error,
          });
        }
      );
      return; // Exit early, will re-run when filters are loaded
    }

    // Step 2: Fetch data for each quarter once filters are loaded
    if (
      funnelFilters !== "" &&
      Object.keys(winRateDataByQuarter).length === 0 &&
      !fetchingRef.current &&
      user.currentFiscalQuarter &&
      isFirstLevelTeamsLoaded &&
      isDefaultTeamLoaded
    ) {
      fetchingRef.current = true;

      // Get current quarter from user object
      const currentQuarter = user.currentFiscalQuarter;

      // Get last 3 quarters (in chronological order: oldest to newest)
      const priorQuarters = getNPriorQuarters(currentQuarter, 3).reverse();
      const quartersToFetch = [...priorQuarters, currentQuarter];

      // Helper to extract filters safely whether funnelFilters is an object or array
      const getFilterData = (key) => {
        if (Array.isArray(funnelFilters)) {
          return funnelFilters.filter((f) => f.type === key || f.name === key);
        }
        return (funnelFilters && funnelFilters[key]) || [];
      };

      // Build default filters (similar to Funnel.js)
      const defaultOppType = getDefaultSalesType(getFilterData("Type"), "Type");
      const defaultSolutionCat = getDefaultSalesType(
        getFilterData("solutionCat"),
        "Solution_Cat"
      );

      let baseFilters = [];

      // Company-specific logic
      if (company === "C0008") {
        const salesFilters = getDefaultSalesType(
          getFilterData("salesType"),
          "cross_sell1__c"
        );
        const dealsFilters = getDefaultSalesType(
          getFilterData("dealType"),
          "Type"
        );

        if (salesFilters[0] !== "All") {
          baseFilters.push({ name: "cross_sell1__c", value: salesFilters });
        }
        if (dealsFilters[0] !== "All") {
          baseFilters.push({ name: "Type", value: dealsFilters });
        }
      }

      if (defaultOppType[0] !== "All") {
        baseFilters.push({ name: "Type", value: defaultOppType });
      }

      if (defaultSolutionCat[0] !== "All") {
        baseFilters.push({ name: "solutionCat", value: defaultSolutionCat });
      }

      // Prioritize first-level teams, fallback to default team filter if not available
      const teamsToFilter =
        Array.isArray(firstLevelTeamNames) && firstLevelTeamNames.length > 0
          ? firstLevelTeamNames
          : (Array.isArray(defaultTeamFilter) && defaultTeamFilter.length > 0
              ? defaultTeamFilter
              : []);

      if (Array.isArray(teamsToFilter) && teamsToFilter.length > 0) {
        baseFilters.push({ name: "team", value: teamsToFilter });
      }

      // Store filters for display in RccCard
      setAppliedFilters(baseFilters);

      // Fetch data for each quarter
      const fetchPromises = quartersToFetch.map((quarter) => {
        const filters = [
          { name: "cfq", value: [quarter], string: true },
          { name: "sameTimePriorQtr", value: 1 },
          ...baseFilters,
        ];

        return appService.getFunnelDataNew(filters).then(
          (dataResp) => {
            // Extract win rates from the last object in the funnel data array
            // Response structure: { message: [...] } where message is the funnel data array
            const funnelData = dataResp.message || dataResp || [];
            if (Array.isArray(funnelData) && funnelData.length > 0) {
              const lastStage = funnelData[funnelData.length - 1];
              return {
                quarter,
                winRateByOppCount: lastStage.rate
                  ? Math.round(lastStage.rate * 100)
                  : 0,
                winRateByACV: lastStage.acvRate
                  ? Math.round(lastStage.acvRate * 100)
                  : 0,
              };
            }
            return {
              quarter,
              winRateByOppCount: 0,
              winRateByACV: 0,
            };
          },
          (error) => {
            console.error(`Error fetching data for ${quarter}:`, error);
            return {
              quarter,
              winRateByOppCount: 0,
              winRateByACV: 0,
            };
          }
        );
      });

      Promise.all(fetchPromises)
        .then((results) => {
          const dataByQuarter = {};
          results.forEach((result) => {
            dataByQuarter[result.quarter] = result;
          });
          // Reset ref AFTER setting data to avoid double-triggering while state updates
          fetchingRef.current = false;
          setWinRateDataByQuarter(dataByQuarter);
        })
        .catch((error) => {
          console.error("Error fetching win rate data:", error);
          fetchingRef.current = false;
        });
    }
  }, [
    funnelFilters,
    company,
    user,
    dispatch,
    isFirstLevelTeamsLoaded,
    isDefaultTeamLoaded,
    firstLevelTeamNames,
    defaultTeamFilter,
  ]);

  // Transform data for chart
  const chartData = useMemo(() => {
    if (isLoading || Object.keys(winRateDataByQuarter).length === 0) {
      return [];
    }

    // Transform data to chart format
    return Object.values(winRateDataByQuarter).sort((a, b) =>
      a.quarter.localeCompare(b.quarter)
    );
  }, [winRateDataByQuarter, isLoading]);

  /**
   * Dynamic y-axis max for better vertical space utilization.
   * Inputs: chartData values (0-100).
   * Output: number in [10, 100] with small headroom.
   */
  const yAxisMax = useMemo(() => {
    if (!chartData.length) return 100;

    let maxVal = 0;
    for (const d of chartData) {
      const byCount = Number(d.winRateByOppCount) || 0;
      const byACV = Number(d.winRateByACV) || 0;
      maxVal = Math.max(maxVal, byCount, byACV);
    }

    // Avoid a zero-height scale when all values are 0.
    if (maxVal <= 0) return 10;

    // Add headroom and round up to nearest 5 (keeps ticks readable).
    const padded = Math.ceil((maxVal * 1.15) / 5) * 5;
    return Math.max(10, Math.min(100, padded));
  }, [chartData]);

  // Series configuration
  const series = ["winRateByOppCount", "winRateByACV"];
  const colorPalette = {
    winRateByOppCount: rccColors.stackedBarPairedPalette[0].color, // Lighter blue
    winRateByACV: rccColors.stackedBarPairedPalette[1].color, // Darker blue
  };
  const legendLabels = {
    winRateByOppCount: "Win Rate by Opp Count",
    winRateByACV: `Win Rate by ${valueLabel}`,
  };

  return (
    <RccCard
      title={title || "Win Rate Trend"}
      isLoading={isLoading}
      redirectToDashboard={redirectToDashboard || "Win Rate Analysis"}
      appliedFilters={appliedFilters}
      focusVariant={"current"}
      focusLabel={"Current Qtr Trend"}
    >
      <div
        style={{
          flex: 1,
          width: "100%",
          minHeight: "300px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {chartData.length > 0 ? (
          <D3GroupedBar
            data={chartData}
            series={series}
            colorPalette={colorPalette}
            options={{
              yAxisFormatter: (v) => `${v}%`,
              valueFormatter: (v) => `${v}%`,
              showValueLabels: true,
              showLegend: true,
              legendLabels: legendLabels,
              showGridlines: false,
              yAxisMax,
            }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              color: rccColors.neutral[400],
            }}
          >
            No data available
          </div>
        )}
      </div>
    </RccCard>
  );
}

export default WinRateTrend;
